import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Paper, 
  InputAdornment, 
  IconButton,
  Alert,
  CircularProgress,
  useTheme
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useAuth } from './AuthContext';
import logo from '../logo6.png';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState('');
  
  const { login, isAuthenticated, isLoading, error, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Fixed: Default to /management/transactions instead of just /
  const from = location.state?.from?.pathname || '/management/transactions';

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  useEffect(() => {
    if (error) clearError();
    setLocalError('');
  }, [username, password]);

  // Clear any autofilled values & disable browser autofill behavior
  useEffect(() => {
    const inputs = document.querySelectorAll('input[type="text"], input[type="password"]');
    inputs.forEach((input) => {
      input.setAttribute('autocomplete', 'off');
      input.value = '';
    });
  }, []);

  const validateForm = () => {
    if (!username.trim()) {
      setLocalError('Username is required');
      return false;
    }
    if (!password.trim()) {
      setLocalError('Password is required');
      return false;
    }
    if (password.length < 3) {
      setLocalError('Password must be at least 3 characters long');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');

    if (!validateForm()) return;

    try {
      const result = await login(username.trim(), password);
      if (result.success) {
        console.log('Login successful, redirecting to:', from);
        // Navigation happens automatically via useEffect above
      } else {
        setLocalError(result.error || 'Login failed. Please try again.');
      }
    } catch (err) {
      console.error('Login error:', err);
      setLocalError('An unexpected error occurred. Please try again.');
    }
  };

  const handleForgotPassword = () => {
    console.log('Forgot password clicked');
    // navigate('/forgot-password');
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          width: '100vw',
          backgroundColor: theme.palette.background.default,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={50} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        width: '100vw',
        backgroundColor: theme.palette.background.default,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Paper
        elevation={8}
        sx={{
          display: 'flex',
          borderRadius: 2,
          overflow: 'hidden',
          width: '800px',
          height: '500px',
          backgroundColor: theme.palette.background.paper,
        }}
      >
        {/* Left side - Gradient with Logo */}
        <Box
          sx={{
            width: '40%',
            background: 'linear-gradient(135deg, #1e88e5 0%, #e53935 50%, #43a047 100%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 3,
            position: 'relative',
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="Company Logo"
            sx={{
              width: 120,
              height: 'auto',
              mb: 3,
              mt: -5,
            }}
          />
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h6"
              sx={{
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1.2rem',
                lineHeight: 1.2,
              }}
            >
              LIFT
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1.2rem',
                lineHeight: 1.2,
              }}
            >
              EMPOWERMENT
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1.2rem',
                lineHeight: 1.2,
              }}
            >
              SOLUTIONS
            </Typography>
          </Box>
        </Box>

        {/* Right side - Login Form */}
        <Box
          sx={{
            width: '60%',
            backgroundColor: theme.palette.background.paper,
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Typography 
            variant="h4" 
            sx={{ 
              mb: 1,
              color: theme.palette.text.primary,
            }}
          >
            Welcome Back
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              mb: 4,
              color: theme.palette.text.secondary,
            }}
          >
            Please sign in to your account.
          </Typography>

          {(error || localError) && (
            <Alert 
              severity="error" 
              sx={{ mb: 3 }}
              onClose={() => {
                clearError();
                setLocalError('');
              }}
            >
              {localError || error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} autoComplete="off">
            {/* Hidden fake fields prevent Chrome autofill */}
            <input
              type="text"
              name="fakeusernameremembered"
              style={{ display: 'none' }}
              autoComplete="off"
            />
            <input
              type="password"
              name="fakepasswordremembered"
              style={{ display: 'none' }}
              autoComplete="off"
            />

            <TextField
              fullWidth
              label="Username"
              variant="outlined"
              margin="normal"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={isLoading}
              autoComplete="new-username"
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: theme.palette.text.secondary }} />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              autoComplete="new-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: theme.palette.text.secondary }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: theme.palette.text.secondary }}
                      disabled={isLoading}
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 4 }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={isLoading}
              sx={{
                mt: 2,
                height: '48px',
                backgroundColor: '#6a5acd',
                color: '#ffffff',
                '&:hover': {
                  backgroundColor: '#5648b8',
                },
                '&:disabled': {
                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.12)' : '#cccccc',
                  color: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
                },
                borderRadius: '8px',
                position: 'relative',
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'LOG IN'
              )}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography 
                variant="body2" 
                onClick={handleForgotPassword}
                sx={{ 
                  color: theme.palette.primary.main,
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Forgot password?
              </Typography>
            </Box>
          </form>
        </Box>
      </Paper>
    </Box>
  );
};

export default Login;